import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Upload, Plus, Trash2, CheckCircle } from 'lucide-react';

interface Author {
  name: string;
  institution: string;
}

interface FormData {
  title: string;
  author: string;
  institution: string;
  email: string;
  phone: string;
  abstract: string;
  presentation_type: string;
  keywords: string;
  document_url?: string;
  coauthors: Author[];
}

export function SymposiumSubmissionPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    author: '',
    institution: '',
    email: '',
    phone: '',
    abstract: '',
    presentation_type: 'Usmeno izlaganje',
    keywords: '',
    coauthors: []
  });

  // Inicijalizacija bucketa pri učitavanju komponente
  useEffect(() => {
    async function initializeStorage() {
      try {
        setIsInitializing(true);
        console.log('Započinjem inicijalizaciju storage-a...');
        
        // Prvo provjeri postoji li bucket
        const { data: buckets, error: bucketsError } = await supabase
          .storage
          .listBuckets();
        
        if (bucketsError) {
          console.error('Error getting buckets:', bucketsError);
          toast.error('Greška pri dohvaćanju bucketa.');
          setIsInitializing(false);
          return;
        }
        
        console.log('Dostupni bucketi:', buckets?.map(b => b.name) || []);
        const symposiumBucketExists = buckets?.some(bucket => bucket.name === 'symposium_documents');
        
        // Ako bucket ne postoji, pokušaj ga kreirati
        if (!symposiumBucketExists) {
          console.log('Creating symposium_documents bucket...');
          // Kreiraj bucket ako ne postoji
          const { data, error } = await supabase
            .storage
            .createBucket('symposium_documents', {
              public: true, 
              fileSizeLimit: 26214400, // 25MB u bajtovima
              allowedMimeTypes: ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
            });
          
          if (error) {
            console.error('Error creating bucket:', error);
            // Ako je greška zbog RLS pravila, nastavite s radom - bucket vjerojatno već postoji ali korisnik nema pravo vidjeti/kreirati
            if (error.message.includes('row-level security')) {
              console.log('RLS error detected when creating bucket. Continuing with existing bucket.');
              // Nastavi dalje - ne prikazujemo error korisniku jer ga to ne treba brinuti
            } else {
              toast.error('Greška pri inicijalizaciji sustava. Molimo pokušajte kasnije.');
            }
          } else {
            console.log('Bucket successfully created');
          }
        } else {
          console.log('Bucket symposium_documents already exists');
        }
      } catch (err) {
        console.error('Error during storage initialization:', err);
        toast.error('Greška pri inicijalizaciji sustava. Molimo pokušajte kasnije.');
      } finally {
        console.log('Inicijalizacija storage-a završena');
        setIsInitializing(false);
      }
    }
    
    initializeStorage();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        toast.error('Datoteka je prevelika. Maksimalna veličina je 25MB.');
        return;
      }
      
      // Provjera formata datoteke
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'pdf' && fileExt !== 'pptx' && fileExt !== 'doc' && fileExt !== 'docx') {
        toast.error('Dozvoljeni su samo .pdf, .pptx, .doc i .docx formati.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    try {
      console.log('======= UPLOAD FILE STARTED =======');
      // Osiguravanje jedinstvenog imena datoteke
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `symposium_document_${Date.now()}_${Math.floor(Math.random() * 1000)}.${fileExt}`;
      
      console.log(`Priprema uploada datoteke: ${fileName} (${file.size} bytes)`);
      
      let contentType = 'application/octet-stream';
      if (fileExt === 'pdf') {
        contentType = 'application/pdf';
      } else if (fileExt === 'pptx') {
        contentType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      } else if (fileExt === 'doc') {
        contentType = 'application/msword';
      } else if (fileExt === 'docx') {
        contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      }
      
      // Priprema parametara za upload
      const uploadOptions = {
        contentType: contentType,
        cacheControl: '3600',
        upsert: false,
      };
      
      // Pokušaj uploada datoteke s definiranim tipom sadržaja
      const { data, error } = await supabase.storage
        .from('symposium_documents')
        .upload(fileName, file, uploadOptions);

      if (error) {
        console.error('Storage upload error:', error);
        throw new Error(`Upload error: ${error.message}`);
      }
      
      // Dohvaćanje javnog URL-a
      const { data: urlData } = supabase.storage
        .from('symposium_documents')
        .getPublicUrl(fileName);
      
      if (!urlData || !urlData.publicUrl) {
        throw new Error('Nije moguće dobiti javni URL za datoteku');
      }

      console.log('======= UPLOAD FILE COMPLETED =======');
      return urlData.publicUrl;
    } catch (error: any) {
      console.error('======= UPLOAD FILE FAILED =======');
      console.error('Error in uploadFile function:', error);
      throw error;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCoauthorChange = (index: number, field: keyof Author, value: string) => {
    const updatedCoauthors = [...formData.coauthors];
    updatedCoauthors[index] = {
      ...updatedCoauthors[index],
      [field]: value
    };
    setFormData(prev => ({ ...prev, coauthors: updatedCoauthors }));
  };

  const addCoauthor = () => {
    setFormData(prev => ({
      ...prev,
      coauthors: [...prev.coauthors, { name: '', institution: '' }]
    }));
  };

  const removeCoauthor = (index: number) => {
    const updatedCoauthors = [...formData.coauthors];
    updatedCoauthors.splice(index, 1);
    setFormData(prev => ({ ...prev, coauthors: updatedCoauthors }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validacija
    const requiredFields = ['title', 'author', 'institution', 'email', 'abstract'];
    for (const field of requiredFields) {
      if (!formData[field as keyof FormData]) {
        toast.error(`Molimo unesite polje "${field === 'title' ? 'naslov' : 
                                          field === 'author' ? 'autor' : 
                                          field === 'institution' ? 'institucija' : 
                                          field === 'email' ? 'email' : 
                                          'sažetak'}"`);
        return;
      }
    }

    // Validacija koautora
    for (let i = 0; i < formData.coauthors.length; i++) {
      const coauthor = formData.coauthors[i];
      if (!coauthor.name || !coauthor.institution) {
        toast.error(`Molimo popunite ime i instituciju za koautora ${i + 1}`);
        return;
      }
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Molimo unesite ispravnu email adresu');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload dokumenta ako je odabran
      let documentUrl = '';
      if (selectedFile) {
        documentUrl = await uploadFile(selectedFile);
      }

      // Priprema podataka za Supabase
      const coauthorsData = formData.coauthors.length > 0 
        ? JSON.stringify(formData.coauthors) 
        : null;

      // Spremi u bazu podataka
      const { data, error } = await supabase
        .from('symposium_registrations')
        .insert([
          {
            title: formData.title,
            author: formData.author,
            institution: formData.institution,
            email: formData.email,
            phone: formData.phone,
            abstract: formData.abstract,
            presentation_type: formData.presentation_type,
            keywords: formData.keywords,
            document_url: documentUrl || null,
            coauthors: coauthorsData
          }
        ]);

      if (error) {
        console.error('Error inserting data:', error);
        throw error;
      }

      toast.success('Sažetak je uspješno prijavljen!');
      
      // Spremi podatke za prikaz potvrde
      setSubmittedData({...formData, document_url: documentUrl});
      setSubmissionSuccessful(true);
      
      // Resetiraj formu
      setFormData({
        title: '',
        author: '',
        institution: '',
        email: '',
        phone: '',
        abstract: '',
        presentation_type: 'Usmeno izlaganje',
        keywords: '',
        coauthors: []
      });
      setSelectedFile(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Došlo je do greške prilikom prijave sažetka. Molimo pokušajte ponovno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Komponenta za prikaz potvrde prijave
  const SubmissionConfirmation = () => {
    if (!submittedData) return null;
    
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-center">
            Uspješna prijava sažetka!
          </h1>
          <p className="text-green-100 text-center mt-2">
            Hvala vam na vašoj prijavi za simpozij "Multidisciplinarni pristup u liječenju tumora probavnih organa"
          </p>
        </div>

        <div className="p-8">
          <div className="mb-8 bg-green-50 p-6 rounded-lg border border-green-200">
            <p className="text-gray-800">
              Vaš sažetak je uspješno zaprimljen. Svi podaci su pohranjeni u našoj bazi podataka.
              Stručni odbor će pregledati vaš sažetak i obavijestiti vas o statusu prijave putem email adrese koju ste naveli.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Pregled prijavljenih podataka</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Osnovni podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Naslov predavanja</p>
                    <p className="text-gray-900 font-medium">{submittedData.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vrsta prezentacije</p>
                    <p className="text-gray-900 font-medium">{submittedData.presentation_type}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Podaci o autoru</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Glavni autor</p>
                    <p className="text-gray-900 font-medium">{submittedData.author}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Institucija</p>
                    <p className="text-gray-900 font-medium">{submittedData.institution}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900 font-medium">{submittedData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="text-gray-900 font-medium">{submittedData.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {submittedData.coauthors.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Koautori</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {submittedData.coauthors.map((coauthor, index) => (
                    <div key={index} className="mb-3 pb-3 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                      <p className="text-gray-900 font-medium">
                        {index + 1}. {coauthor.name} ({coauthor.institution})
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Sažetak</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-line">{submittedData.abstract}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Ključne riječi</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900">{submittedData.keywords}</p>
              </div>
            </div>

            {submittedData.document_url && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Priloženi dokument</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <a 
                    href={submittedData.document_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Pregledaj dokument
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => setSubmissionSuccessful(false)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Podnesi novi sažetak
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4">
        {submissionSuccessful ? (
          <SubmissionConfirmation />
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
              <h1 className="text-3xl font-bold text-center">
                Prijava sažetka za simpozij
              </h1>
              <p className="text-blue-100 text-center mt-2">
                "Multidisciplinarni pristup u liječenju tumora probavnih organa"
              </p>
            </div>

            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Naslov predavanja *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite naslov vašeg predavanja"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Glavni autor *</label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ime i prezime glavnog autora"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Institucija *</label>
                  <input
                    type="text"
                    name="institution"
                    value={formData.institution}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Naziv institucije glavnog autora"
                  />
                </div>

                {/* Koautori */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-gray-700 font-medium">Koautori</label>
                    <button
                      type="button"
                      onClick={addCoauthor}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Dodaj koautora
                    </button>
                  </div>
                  
                  {formData.coauthors.length > 0 && (
                    <div className="space-y-4 border border-blue-100 rounded-lg p-4 bg-blue-50">
                      {formData.coauthors.map((coauthor, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-sm">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-lg font-medium text-blue-900">Koautor {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeCoauthor(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-700 text-sm font-medium mb-1">
                                Ime i prezime koautora *
                              </label>
                              <input
                                type="text"
                                value={coauthor.name}
                                onChange={(e) => handleCoauthorChange(index, 'name', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Ime i prezime koautora"
                              />
                            </div>
                            <div>
                              <label className="block text-gray-700 text-sm font-medium mb-1">
                                Institucija koautora *
                              </label>
                              <input
                                type="text"
                                value={coauthor.institution}
                                onChange={(e) => handleCoauthorChange(index, 'institution', e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Naziv institucije koautora"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Vaša email adresa"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Vaš kontakt telefon"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Vrsta prezentacije</label>
                  <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                    Usmeno izlaganje
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Sažetak *</label>
                  <textarea
                    name="abstract"
                    value={formData.abstract}
                    onChange={handleChange}
                    rows={6}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite sažetak vašeg predavanja (maksimalno 350 riječi)"
                  ></textarea>
                </div>

                {/* Ključne riječi */}
                <div className="border-2 border-blue-200 p-4 rounded-lg bg-blue-50">
                  <label className="block text-gray-700 font-medium mb-2">Ključne riječi *</label>
                  <input
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite ključne riječi odvojene zarezom (npr. kardiologija, hipertenzija, terapija)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Unesite 3-5 ključnih riječi odvojenih zarezom</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Upload dokumenta (opciono)</label>
                  <div className="flex flex-col items-center justify-center w-full">
                    <label
                      htmlFor="file-upload"
                      className="w-full flex flex-col items-center justify-center px-4 py-6 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                    >
                      <Upload className="w-12 h-12 text-blue-500 mb-3" />
                      <div className="flex flex-col items-center">
                        <p className="mb-2 text-sm text-blue-500">
                          <span className="font-semibold">Kliknite za upload</span> ili povucite i ispustite
                        </p>
                        <p className="text-xs text-gray-500">
                          PDF, PPTX, DOC ili DOCX (max. 25MB)
                        </p>
                      </div>
                      {selectedFile && (
                        <div className="mt-4 text-sm text-blue-600">
                          Odabrana datoteka: {selectedFile.name}
                        </div>
                      )}
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="hidden"
                        accept=".pdf,.pptx,.doc,.docx"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Napomena: Možete odmah priložiti dokument (prezentaciju ili rad) ili ga naknadno učitati putem posebnog obrasca. 
                    Sažetak će biti recenziran, a vi ćete biti obaviješteni o prihvaćanju sažetka.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting || isInitializing}
                    className={`px-6 py-3 text-white rounded-lg font-medium transition-colors
                      ${isSubmitting || isInitializing 
                        ? 'bg-blue-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {isInitializing 
                      ? 'Inicijalizacija...' 
                      : isSubmitting 
                        ? 'Predavanje sažetka...' 
                        : 'Predaj sažetak'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 